<template>
  <div class="tw-mb-6">
    <base-table :loading="isLoading" :fields="fields" :items="items" :overflowVisible="true">
      <template #dueDate="{item: todoItem}">
        <div class="tw-flex tw-items-center tw-text-xs tw-text-gray-600 tw-whitespace-nowrap">
          <div v-if="dueDateStatus(todoItem.dueDate) === 'today'">
            <base-badge size="sm" color="yellow">{{ customDateFormatter(todoItem.dueDate) }}</base-badge>
          </div>
          <div v-else-if="dueDateStatus(todoItem.dueDate) === 'overdue'">
            <base-badge size="sm" color="red">{{ customDateFormatter(todoItem.dueDate) }}</base-badge>
          </div>
          <div v-else>
            <base-badge size="sm" color="gray">{{ customDateFormatter(todoItem.dueDate) }}</base-badge>
          </div>
        </div>
      </template>
      <template #todoItem="{item: todoItem}">
        <div class="tw-flex">
          <div>
            <div class="tw-flex tw-flex-col">
              <div class="tw-flex tw-font-normal tw-text-gray-700 tw-text-base tw-whitespace-nowrap">{{ todoItem.todoItemTypeLabel }}</div>
              <div class="tw-flex tw-font-normal tw-text-xs tw-whitespace-nowrap">
                <base-button type="text" size="md" @click="gotoMember(todoItem.targetEntityId)">{{ todoItem.targetName }}</base-button>
              </div>
              <div class="tw-flex tw-mb-1.5 tw-text-xs tw-text-gray-500 tw-whitespace-nowrap">{{ todoItem.locations.length > 0 ? todoItem.locations[0].name : "" }}</div>
            </div>
          </div>
        </div>
      </template>
      <template #assigned="{item: todoItem}">
        <div class="tw-flex tw-justify-center">
          <assigned-user-button :todoItem="todoItem" :businessUsers="businessUsers" @assign="assign" />
        </div>
      </template>
      <template #contact="{item: todoItem}">
        <div class="tw-flex-col tw-items-start">
          <base-button v-if="todoItem.targetPhoneNumber" type="flat" size="xs" @click="call(todoItem.targetPhoneNumber)">
            <template v-slot:icon>
              <phone-icon />
            </template>
            <base-phone-number-field class="tw-hidden lg:tw-block" :value="todoItem.targetPhoneNumber" />
          </base-button>
          <base-button v-if="todoItem.targetEmailAddress" type="flat" size="xs" @click="email(todoItem.targetEmailAddress)">
            <template v-slot:icon>
              <mail-icon />
            </template>
            <span class="tw-hidden lg:tw-block">{{ todoItem.targetEmailAddress }}</span>
          </base-button>
        </div>
      </template>
      <template #actions="{item: todoItem}">
        <div class="tw-flex tw-justify-center tw-space-x-1">
          <base-button type="flat" size="sm" @click="history(todoItem.todoItemId)">
            <template v-slot:icon>
              <list-icon />
            </template>
            Details<base-badge v-show="todoItem.numComments > 0" class="tw-ml-1" color="primary" size="sm">{{ todoItem.numComments }}</base-badge>
          </base-button>
          <base-button
            v-if="todoItem.todoItemTypeConstant === 'ONLINE_SUPPORT_QUESTION'"
            type="flat"
            size="sm"
            @click="comment(todoItem.todoItemId, todoItem.todoItemTypeConstant)"
          >
            <template v-slot:icon>
              <comment-icon />
            </template>
            Reply
          </base-button>
          <base-button v-else type="flat" size="sm" @click="comment(todoItem.todoItemId, todoItem.todoItemTypeConstant)">
            <template v-slot:icon>
              <edit-icon />
            </template>
            Comment
          </base-button>
          <base-button v-if="category !== 'completed' && todoItem.todoItemTypeConstant !== 'RX_AUTHORIZATION'" type="flat" size="sm" @click="complete(todoItem.todoItemId)">
            <template v-slot:icon>
              <check-icon />
            </template>
            Complete
          </base-button>

          <base-button v-if="isLocationFiltered === false" type="flat" size="sm" @click="del(todoItem.todoItemId)">
            <template v-slot:icon>
              <trash-icon />
            </template>
            Delete
          </base-button>
        </div>
      </template>
      <template #approval="{item: todoItem}">
        <div v-if="category !== 'completed' && isLocationFiltered === true" class="tw-flex tw-justify-center tw-space-x-2">
          <base-dropdown class="tw-my-auto" :options="['Approve', 'Approve with changes']" :showBackground="false" color="green" type="text" align="right">
            <base-icon color="green" :loading="isLoading">
              <check-icon class="tw-h-5 tw-w-5" />
            </base-icon>

            <template #option="{ option: link }">
              <button
                class="tw-flex tw-items-center tw-w-full tw-text-left tw-px-4 tw-py-2 tw-text-sm tw-text-gray-600 focus:tw-outline-none"
                @click="link === 'Approve' ? approve(todoItem.todoItemId) : approveChanges(todoItem)"
              >
                {{ link }}
              </button>
            </template>
          </base-dropdown>
          <base-icon color="red" @click="deny(todoItem.todoItemId)" class="tw-my-auto"><x-icon class="tw-h-5 tw-w-5 " /> </base-icon>
        </div>
      </template>
      <template #empty-state>
        <div class="tw-flex tw-justify-center tw-py-5">
          No Results
        </div>
      </template>
      <template #loading-row>
        <td class="tw-py-3 tw-px-4"><base-skeleton-loader class="tw-h-6 tw-w-12 " /></td>
        <td class="tw-flex tw-py-3 tw-justify-center"><base-skeleton-loader class="tw-h-9 tw-w-9 tw-rounded-full" /></td>
        <td class="tw-py-3 tw-pl-4 tw-pr-8"><base-skeleton-loader class="tw-h-4 tw-w-40" /><base-skeleton-loader class="tw-h-2 tw-w-20 mt-2" /></td>
        <td class="tw-py-3 tw-px-4">
          <div class="tw-flex"><base-skeleton-loader class="tw-h-3 tw-w-3 tw-mr-1" /><base-skeleton-loader class="tw-h-3 tw-w-20" /></div>
          <div class="tw-flex tw-mt-2"><base-skeleton-loader class="tw-h-3 tw-w-3 tw-mr-1" /><base-skeleton-loader class="tw-h-3 tw-w-24" /></div>
        </td>
        <td class="tw-py-3 tw-px-4">
          <div class="tw-flex tw-space-x-2 tw-justify-center">
            <base-skeleton-loader class="tw-h-8 tw-w-20" v-for="key in 4" :key="`action-loader-cell-${key}`" />
          </div>
        </td>
        <td v-if="isLocationFiltered === true" class="tw-py-3 tw-px-4 tw-flex tw-space-x-2">
          <base-skeleton-loader class="tw-h-8 tw-w-8 tw-rounded-full" /><base-skeleton-loader class="tw-h-8 tw-w-8 tw-rounded-full" />
        </td>
      </template>
    </base-table>
    <base-pagination v-if="page.total > page.take" v-model="page" class="tw-mt-2" />
    <approve-rx-auth-with-changes ref="approveChangesSlideover" :businessId="businessId" @save="loadPage" />
  </div>
</template>

<script>
import moment from "moment";
import { PhoneIcon, MailIcon, CheckIcon, EditIcon, TrashIcon, ListIcon, CommentIcon, XIcon } from "@/assets/svgs";
import { mapGetters } from "vuex";
import AssignedUserButton from "@/views/business/todo/components/AssignedUserButton";
import ApproveRxAuthWithChanges from "@/views/business/todo/components/ApproveRxAuthWithChanges";
import { debounce } from "debounce";

export default {
  name: "ItemsList",
  props: {
    businessId: String,
    category: String,
    showRows: { type: Number, default: 10 },
    query: String,
    filters: Object,
    targetEntityId: String,
  },
  components: { PhoneIcon, MailIcon, CheckIcon, EditIcon, TrashIcon, AssignedUserButton, ListIcon, CommentIcon, XIcon, ApproveRxAuthWithChanges },
  data() {
    return {
      isLoading: true,
      page: {
        take: this.showRows,
        skip: 0,
        total: 0,
      },
      businessUsers: [],
      items: [],
    };
  },
  computed: {
    fields() {
      let fields = [
        { title: "Due", key: "dueDate", thClass: "tw-w-0 tw-text-left" },
        { title: "Assigned", key: "assigned", thClass: "tw-w-0 tw-text-center" },
        { title: "Activity", key: "todoItem", thClass: "tw-w-0 tw-text-left" },
        { title: "Contact", key: "contact", thClass: "tw-w-0 tw-text-left" },
        { title: "Actions", key: "actions", thClass: "tw-w-0 tw-text-center" },
      ];

      if (this.isLocationFiltered === true) fields.push({ title: "Approval", key: "approval", thClass: "tw-w-0 tw-text-center" });
      return fields;
    },
    ...mapGetters("cognito", { userId: "id" }),
    ...mapGetters("role", { isLocationFiltered: "isLocationFiltered" }),
    title() {
      if (this.category === "me") return "Assigned to me";
      else if (this.category === "unassigned") return "Unassigned";
      else if (this.category === "other") return "Other Assigned";
      else if (this.category === "completed") return "Completed";
      else return "unknown";
    },
    url() {
      if (this.category === "me") return `/businesses/${this.businessId}/todo/assignedself`;
      else if (this.category === "unassigned") return `/businesses/${this.businessId}/todo/unassigned`;
      else if (this.category === "other") return `/businesses/${this.businessId}/todo/assignedother`;
      else if (this.category === "completed") return `/businesses/${this.businessId}/todo/completed`;
      else return `/businesses/${this.businessId}/todo/open`;
    },
    actionOptions() {
      const options = [];
      options.push(
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        { title: "Complete", eventName: "complete" },
      );

      return options;
    },
  },
  watch: {
    page: {
      handler() {
        this.loadPage();
      },
    },
    query() {
      this.page.skip = 0;
      this.debouncedLoadPage();
    },
    filters: function(newValue, oldValue) {
      if (newValue !== oldValue) this.loadPage();
    },
  },
  created() {
    this.loadPage();
    this.loadBusinessUsers();
    this.debouncedLoadPage = debounce(this.loadPage, 500);
  },
  mounted() {
    this.EventBus.$on("activitiesUpdated", this.loadPage);
  },
  methods: {
    refresh() {
      this.loadPage();
    },
    getUser(userId) {
      if (userId && this.businessUsers.length > 0) return this.businessUsers.find((u) => u.userId === userId).initials;
      else return "";
    },
    call(phoneNumber) {
      window.open("tel:" + phoneNumber);
    },
    email(emailAddress) {
      window.open("mailto:" + emailAddress);
    },
    del(item) {
      this.$emit("del", item);
    },
    comment(item, constant) {
      this.$emit("comment", { todoItemId: item, constant: constant });
    },
    history(item) {
      this.$emit("history", item);
    },
    assign(item) {
      this.$emit("assign", item);
    },
    approve(item) {
      this.$emit("approve", item);
    },
    approveChanges(item) {
      this.$refs.approveChangesSlideover.show(item.todoItemId, item.targetEntityId);
    },
    deny(item) {
      this.$emit("deny", item);
    },
    loadPage() {
      this.isLoading = true;

      let url = this.url;
      url += `?skip=${this.page.skip}&take=${this.page.take}&query=${encodeURIComponent(this.query || "")}`;

      if (this.targetEntityId != null) url += `&targetEntityId=${this.targetEntityId}`;

      let filters = { locationsFilter: [], typesFilter: [] };
      if (this.filters !== undefined) filters = this.filters;

      this.$axios
        .post(url, { locationsFilter: filters.locationsFilter, typesFilter: filters.typesFilter, assignedToFilter: filters.assignedToFilter })
        .then((res) => {
          this.page.take = res.data.take;
          this.page.skip = res.data.skip;
          this.page.total = res.data.total;
          this.items = res.data.items;
        })
        .catch((err) => {
          this.EventBus.$emit("GLOBAL_ERROR", {
            title: "Failed to load tasks",
            message: "We had a problem retrieving the list of tasks. Please reload the page to try again.",
            error: err,
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    loadBusinessUsers() {
      this.$axios.get(`/businesses/${this.businessId}/users/all`).then((res) => {
        this.businessUsers = this.transformList(res.data);
      });
    },
    transformList(items) {
      const formattedItems = [];

      if (items) {
        items.forEach(function(item) {
          item.initials = item.fullName
            .match(/(\b\S)?/g)
            .join("")
            .match(/(^\S|\S$)?/g)
            .join("")
            .toUpperCase();

          formattedItems.push(item);
        });
      }

      return formattedItems;
    },
    complete(todoItemId) {
      this.isLoading = true;

      this.$axios
        .put(`/businesses/${this.businessId}/todo/${todoItemId}`, {
          status: "completed",
        })
        .then(() => {
          this.EventBus.$emit("GLOBAL_NOTIFY", {
            title: "Activity Completed",
            message: `Your activity was marked complete.`,
          });
          this.loadPage();
        })
        .catch((err) => {
          this.EventBus.$emit("GLOBAL_ERROR", {
            title: "Error completing activity",
            message: "Sorry, we had trouble completing this activity. The technicians have been notified. Please try again.",
            error: err,
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    getAvatarRef(id) {
      return id;
    },
    customDateFormatter(date) {
      return moment
        .utc(date)
        .local()
        .format("MMM D");
    },
    dueDateStatus(date) {
      if (moment(date).isSame(new Date(), "day")) return "today";
      else if (moment(date).diff(new Date(), "days") > 0) return "future";
      else return "overdue";
    },
    gotoMember(memberId) {
      this.$router.push(`/businesses/${this.businessId}/members/${memberId}/plans`);
    },
  },
};
</script>
